import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Users, Heart, Award, Leaf, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const AProposPage = () => {
  const values = [
    {
      icon: Users,
      title: 'Accompagnement personnalisé',
      description: 'Une écoute attentive et un suivi sur-mesure pour concrétiser votre projet de vie en toute sérénité.'
    },
    {
      icon: Award,
      title: 'Expertise & Qualité',
      description: 'Forte de 40 ans d\'expérience, notre équipe s\'appuie sur un savoir-faire reconnu et des certifications exigeantes.'
    },
    {
      icon: Leaf,
      title: 'Engagement environnemental',
      description: 'Construction durable, maisons biosourcées et maîtrise de l\'impact environnemental pour un habitat responsable.'
    },
    {
      icon: Heart,
      title: 'Bienveillance & Proximité',
      description: 'Une relation de confiance, de convivialité et de transparence dans un esprit familial et humain.'
    }
  ];

  const timeline = [
    {
      year: '1985',
      title: 'Création',
      description: 'Fondation de MRTP SARL à Villars-Colmars, dans un contexte de plein essor du marché de la maison individuelle dans les Alpes-de-Haute-Provence.'
    },
    {
      year: '1990s',
      title: 'Développement',
      description: 'Adoption du CCMI et construction du siège social. Extension de notre zone d\'intervention dans les Alpes du Sud.'
    },
    {
      year: '2000s',
      title: 'Innovation',
      description: 'Obtention des certifications NF et HQE. Lancement d\'un catalogue constructeur évolutif et moderne.'
    },
    {
      year: '2025',
      title: '40 ans d\'excellence',
      description: 'Célébration de 40 ans d\'expertise, marqués par la pérennité, l\'adaptabilité et l\'engagement envers nos clients.'
    }
  ];

  const stats = [
    { number: '40', label: 'Ans d\'expérience' },
    { number: '1', label: 'Atelier principal en Provence' },
    { number: '75+', label: 'Modèles de maisons' },
    { number: '1000+', label: 'Projets réalisés' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div
        className="relative h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?w=1600)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-white max-w-3xl">
            <div className="flex items-center text-sm mb-4">
              <Link to="/" className="opacity-90 hover:opacity-100">Accueil</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span>À propos</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">À propos de MRTP SARL</h1>
            <p className="text-xl">
              Depuis 1985, notre raison d'être est de vous permettre de construire votre maison l'esprit tranquille.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="py-16 bg-[#c4d600]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-gray-800 mb-2">{stat.number}</div>
                <div className="text-gray-800 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Introduction */}
      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-800 mb-6 text-center">Notre histoire</h2>
          <p className="text-lg text-gray-600 mb-4">
            MRTP SARL est un constructeur basé à Villars-Colmars, au cœur des Alpes-de-Haute-Provence. Nous accompagnons les porteurs de projet dans la conception et la réalisation de chalets, maisons de montagne et résidences secondaires adaptées à leur mode de vie.
          </p>
          <p className="text-lg text-gray-600 mb-4">
            Notre ancrage régional nous permet de travailler avec des partenaires locaux et de valoriser les matériaux biosourcés adaptés au climat alpin, tout en conservant la proximité humaine qui fait notre force depuis 1985.
          </p>
          <p className="text-lg text-gray-600">
            Nous proposons un large choix de modèles : maison plain-pied, maison à étage, maison demi-niveau, maison biosourcée, avec un accompagnement personnalisé et une garantie de respect de vos contraintes budgétaires et stylistiques.
          </p>
        </div>
      </div>

      {/* Timeline */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-800 mb-12 text-center">Notre parcours</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {timeline.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow h-full">
                  <div className="w-16 h-16 bg-[#c4d600] rounded-full flex items-center justify-center mb-4 text-gray-800 font-bold text-xl">
                    {item.year}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#c4d600] font-semibold mb-2">NOS VALEURS</p>
            <h2 className="text-4xl font-bold text-gray-800">Ce qui nous guide au quotidien</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="bg-gray-50 rounded-lg p-8 hover:shadow-lg transition-shadow">
                  <div className="flex items-start">
                    <div className="w-16 h-16 bg-[#c4d600] rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                      <Icon className="w-8 h-8 text-gray-800" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800 mb-3">{value.title}</h3>
                      <p className="text-gray-600">{value.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Team Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#c4d600] font-semibold mb-2">NOTRE ÉQUIPE</p>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Une grande famille</h2>
              <p className="text-lg text-gray-600 mb-4">
                MRTP SARL, c'est d'abord une histoire de famille, puis une rencontre d'hommes et de femmes qui œuvrent avec passion, depuis 1985 pour bâtir la maison de vos rêves !
              </p>
              <p className="text-lg text-gray-600 mb-4">
                C'est aussi une aventure humaine où se mêlent professionnalisme, investissement, énergie, convivialité, bonne humeur, écoute, proximité, exigence et bienveillance.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Notre équipe mise sur la complémentarité des compétences, la collaboration et l'engagement pour vous offrir un service de qualité à chaque étape de votre projet.
              </p>
              <Button asChild className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold">
                <Link to="/nos-agences">Rencontrez nos équipes</Link>
              </Button>
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800"
                alt="Notre équipe"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Certifications Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#c4d600] font-semibold mb-2">QUALITÉ & CERTIFICATIONS</p>
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Notre engagement qualité</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Nous nous appuyons sur des certifications exigeantes pour garantir la qualité de nos constructions et la satisfaction de nos clients.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="w-20 h-20 bg-[#c4d600] rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-10 h-10 text-gray-800" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Certification NF</h3>
              <p className="text-gray-600 text-sm">
                Garantie de qualité reconnue pour la construction de maisons individuelles
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="w-20 h-20 bg-[#c4d600] rounded-full flex items-center justify-center mx-auto mb-4">
                <Leaf className="w-10 h-10 text-gray-800" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Certification HQE</h3>
              <p className="text-gray-600 text-sm">
                Haute Qualité Environnementale pour des maisons respectueuses de l'environnement
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="w-20 h-20 bg-[#c4d600] rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-gray-800" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">CCMI</h3>
              <p className="text-gray-600 text-sm">
                Contrat de Construction de Maison Individuelle pour votre protection juridique
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 bg-[#c4d600]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-800 mb-6">
            Faites confiance à notre équipe de professionnels
          </h2>
          <p className="text-xl text-gray-800 mb-8">
            À votre écoute et à vos côtés dans chacune des étapes de votre projet !
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-gray-800 hover:bg-gray-900 text-white font-semibold text-lg px-8 py-6">
              <Link to="/contact">Contactez-nous</Link>
            </Button>
            <Button asChild variant="secondary" className="bg-white hover:bg-gray-100 text-gray-800 font-semibold text-lg px-8 py-6">
              <Link to="/nos-terrains">Découvrir nos terrains</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AProposPage;
